import { NavMainItems, CustomPage, NavProfileItems } from "../types/working-model";

// single source of truth for all routes
export const PATHNAMES = {
	Dashboard: "/admin",
	Events: "/admin/events",
	SelectEvent: "/admin/events/:id/:tab?",
	DetailsEventLink: (id: string): string => `/admin/events/${id}/audience-lists`,
	Analytics: "/admin/analytics/:tab?",
	AnalyticsLink: "/admin/analytics",
	People: "/admin/people",
	Content: "/admin/content",
	ContentSubLevels: {
		Documents: {
			All: "/admin/content/documents/all",
			Pdf: "/admin/content/documents/pdf",
			Word: "/admin/content/documents/word",
			Excel: "/admin/content/documents/excel",
			Powerpoint: "/admin/content/documents/powerpoint",
			Misc: "/admin/content/documents/misc"
		},
		Images: "/admin/content/images",
		Videos: {
			Recorded: "/admin/content/videos/recorded",
			Uploaded: "/admin/content/videos/uploaded"
		},
		Fonts: "/admin/content/fonts",
		Palettes: "/admin/content/palettes",
		Templates: "/admin/content/templates",
	},
	Products: "/admin/products",
	Integrations: "/admin/integrations/:tab?",
	IntegrationsLink: "/admin/integrations",
	Settings: "/admin/settings/:tab?",
	SettingsLink: "/admin/settings",
	AdminProfile: "/admin/profile",
	WhatsNew: "/admin/whats-new",
	Support: "https://help.brand.live/hc/en-us", //external link
	CreateEvent: "/admin/events/create",
	EventInfo: "/admin/events/create/info",
	EventBasePath: "/admin/events/create/:id",
	AudienceList: {
		AudienceList: "/admin/events/create/:id/registration/:language/audience-list/:audienceID",
		AudienceListLink: (id: string, language: string, audienceID: string): string => `/admin/events/create/${id}/registration/${language}/audience-list/${audienceID}`
	},
	Event: {
		Landing: "/admin/events/create/:id/landing/:language?",
		LandingLink: (id: string, language?: string): string => `/admin/events/create/${id}/landing/${language || ""}`,
		Registration: "/admin/events/create/:id/registration/:language/:tab?",
		RegistrationLink: (id: string, language: string, tab?: string): string => `/admin/events/create/${id}/registration/${language}/${tab || ""}`,
		Availability: "/admin/events/create/:id/registration/:language/settings/availability",
		AvailabilityLink: (id: string, language: string): string => `/admin/events/create/${id}/registration/${language}/settings/availability`,
		Ticketing: "/admin/events/create/:id/registration/:language/settings/ticketing",
		TicketingLink: (id: string, language: string): string => `/admin/events/create/${id}/registration/${language}/settings/ticketing`,
		SSORegistrationSettings: "/admin/events/create/:id/registration/:language/settings/sso",
		SSORegistrationSettingsLink: (id: string, language: string): string => `/admin/events/create/${id}/registration/${language}/settings/sso`,
		Event: "/admin/events/create/:id/event/:language?",
		EventLink: (id: string, language?: string): string => `/admin/events/create/${id}/event/${language}`,
		EmailConfirmation: "/admin/events/create/:id/registration/:language/settings/email-confirmation",
		EmailConfirmationLink: (id: string, language: string): string => `/admin/events/create/${id}/registration/${language}/settings/email-confirmation`,
		// the following NavMainItems and NavProfileItems paths correspond to mapped menu items in the event panel
		HomePath: "/admin/events/create/:id/event/:language/home",
		[NavMainItems.Home]: (id: string, language: string): string => `/admin/events/create/${id}/event/${language}/home`,
		SessionsPath: "/admin/events/create/:id/event/:language/sessions/:tab?",
		[NavMainItems.Sessions]: (id: string, language: string, tab?: string): string => `/admin/events/create/${id}/event/${language}/sessions/${tab || ""}`,
		LeaderboardPath: "/admin/events/create/:id/social/:language/leaderboard",
		[NavMainItems.Leaderboard]: (id: string, language: string): string => `/admin/events/create/${id}/social/${language}/leaderboard`,
		PeoplePath: "/admin/events/create/:id/event/people",
		[NavMainItems.People]: (id: string): string => `/admin/events/create/${id}/event/people`,
		// the remaining NavMainItems paths are not included in mvp so they are just placeholders to make typescript happy
		[NavMainItems.TellyClub]: (id: string): string => `/admin/events/create/${id}/event`,
		[NavMainItems.Notifications]: (id: string): string => `/admin/events/create/${id}/event`,
		[NavMainItems.Catalog]: (id: string): string => `/admin/events/create/${id}/event`,
		TermsConditionsPath: "/admin/events/create/:id/event/:language/terms-conditions",
		[NavProfileItems.TermsAndConditions]: (id: string, language: string): string => `/admin/events/create/${id}/event/${language}/terms-conditions`,
		PrivacyPolicyPath: "/admin/events/create/:id/event/:language/privacy-policy",
		[NavProfileItems.PrivacyPolicy]: (id: string, language: string): string => `/admin/events/create/${id}/event/${language}/privacy-policy`,
		// the remaining NavProfileItem path is not included in mvp so it is just a placeholder to make typescript happy
		[NavProfileItems.Profile]: (id: string): string => `/admin/events/create/${id}/event`,
		// conditional routing for theme so the panel can change but the editor view remains
		Theme: "/admin/events/create/:id/event/:language/theme",
		ThemeLanding: "/admin/events/create/:id/event/:language/theme/landing",
		ThemeSession: "/admin/events/create/:id/event/theme/session/:session/:language",
		ThemeLink: (id: string, language?: string, editor?: string): string => `/admin/events/create/${id}/event/${language}/theme/${editor || ""}`,
		CustomPage: "/admin/events/create/:id/pages/:customPath/:language",
		CustomPageLink: (id: string, customPath: string, language: string): string => (`/admin/events/create/${id}/pages/${customPath}/${language}`),
		Social: "/admin/events/create/:id/social/:language",
		SocialLink: (id: string, language: string): string => `/admin/events/create/${id}/social/${language}`,
		EmailList: "/admin/events/create/:id/emails/:language",
		EmailListLink: (id: string, language: string) => `/admin/events/create/${id}/emails/${language}`,
		// FUTURE DEV: EmailDetails isnt used, however keeping for now until we add content editing to emails and decide if we want to use the details page
		EmailDetails: "/admin/events/create/:id/emails/:language/:scheduleUuid/:tab?",
		EmailDetailsLink: (id: string, language: string, scheduleUuid: string, tab: 'content' | 'settings'): string => `/admin/events/create/${id}/emails/${language}/${scheduleUuid}/${tab}`,
		Directory: "/admin/events/create/:id/social/:language/directory",
		DirectoryLink: (id: string, language: string): string => `/admin/events/create/${id}/social/${language}/directory`,
		[NavMainItems.Social]: (id: string, language: string): string => `/admin/events/create/${id}/social/${language}/directory`,
		SocialProfile: "/admin/events/create/:id/social/:language/profile/:tab?",
		SocialProfileLink: (id: string, language: string, tab?: string): string => `/admin/events/create/${id}/social/${language}/profile/${tab || "sections"}`,
		DirectMessagingLink: (id: string, language: string): string => `/admin/events/create/${id}/social/${language}/messaging`,
		DirectMessaging: "/admin/events/create/:id/social/:language/messaging",
		InPersonAttendanceSettingsLink: (id: string, language: string): string => `/admin/events/create/${id}/registration/${language}/settings/in-person-attendance`,
		InPersonAttendanceSettings: "/admin/events/create/:id/registration/:language/settings/in-person-attendance",
	},
	SessionBasePath: "/admin/events/create/:id/session/:session/:language?",
	Session: {
		Session: "/admin/events/create/:id/session/:session/:language/:tab?",
		SessionLink: (id: string, sessionId: string, language: string, tab?: string): string => `/admin/events/create/${id}/session/${sessionId}/${language}/${tab || ""}`,
	},
};

export const getCurrentCustomPage = (pages: CustomPage[] | undefined, currentPath: string | null): CustomPage | null => {
	if (!pages || !currentPath) return null;
	const page = pages.find((page) => page.path === currentPath && !page.deleted);
	return page || null;
};